import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FitScreenOutlinedIcon from '@mui/icons-material/FitScreenOutlined';
import {
  Box,
  CircularProgress,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { SlimUser } from 'api';
import { AttioQueryKeys, COLUMNS_HIDDEN_BY_DEFAULT, COMPANY_NAME_KEY, FINANCIAL_FIELDS_HIDDEN_BY_DEFAULT } from 'common/constants/analyst-dashboards';
import { EMPTY_VALUATIONS_TABLE } from 'common/constants/documents';
import { EventType } from 'context/EventBusContext/EventBusContext.type';
import { useEventSubscription } from 'context/EventBusContext/useEventSubscription';
import { useZoom } from 'context/ZoomContext';
import { ExpandableSearch, ExpandableSearchHandle } from 'dashboard409a/components/ExpandableSearch';
import {
  calculateTableHeight,
  StyledTableCellEmptyMessage,
  useNoteHandler,
} from 'dashboard409a/components/ValuationsTable/common';
import { DealStageOption } from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/ValuationsTableRow.types';
import { useAttioFilters } from 'dashboard409a/hooks/useAttioFilters';
import { useKeyboardNavigation } from 'dashboard409a/hooks/useKeyboardNavigation';
import { useValuationsWithBulkData } from 'dashboard409a/hooks/useValuationsWithBulkData';
import { columns } from 'dashboard409a/pages/Workboard/common/colConfig';
import { JobFilters } from 'dashboard409a/pages/Workboard/Filters';
import {
  getDealStatusOptions,
  getValuationStatusOptions,
  updateValuationDealStage,
} from 'dashboard409a/services/attio-common';
import { CreateNoteRequest, SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { RootStateType } from 'dashboard409a/states/store';
import {
  getInitialColumnVisibilityFromStorage,
  saveColumnVisibilityToStorage,
} from 'dashboard409a/utilities/columnVisibilityStorage';
import { useDebouncedValue } from 'hooks';
import { useLocalStorage } from 'services/hooks/useLocalStorage';
import { Logger } from 'utilities/logger';
import ConfigToggler from './ConfigToggler/ConfigToggler';
import {
  EnhancedTableHeadColumn,
  ValuationsTableBulkDataProps,
  ValuationsTableValuationUpdatedPayload,
} from './ValuationsTable.types';
import { StyledUserAutocomplete, userAutocompleteSlotProps } from './ValuationsTableBulkData.styles';
import ValuationsTableHead from './ValuationsTableHead';
import ValuationsTableRow from './ValuationsTableRow';

export const ValuationsTableBulkData = ({
  filters,
  orderBy,
  order,
  sortCriteria,
  onRequestSort,
  onRowClick,
  sectionId,
  pauseBatchLoading = false,
  toolbarPrefix,
  hideTable = false,
}: ValuationsTableBulkDataProps) => {
  const isInitialMount = useRef(true);
  const previousSectionId = useRef<string | undefined>(sectionId);

  const searchRef = useRef<ExpandableSearchHandle>(null);
  const [searchParam, setSearchParam] = useState({ searchParam: '', searchAll: false });
  const debouncedSearchParam = useDebouncedValue(searchParam.searchParam, 300);

  // ── Note cell selection state ────────────────────────────────────────────
  const [selectedNoteIds, setSelectedNoteIds] = useState<Set<string>>(new Set());
  const [lastSelectedNoteId, setLastSelectedNoteId] = useState<string | null>(null);
  const isDraggingRef = useRef(false);
  const dragAnchorIdRef = useRef<string | null>(null);

  // Stable ref so the global paste handler never becomes stale
  const pasteHandlerRef = useRef<(text: string) => Promise<void>>(async () => {});

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        searchRef.current?.expand();
        return;
      }

      // ── Paste to selected note cells ───────────────────────────────────
      if ((event.metaKey || event.ctrlKey) && event.key === 'v') {
        const activeEl = document.activeElement as HTMLElement | null;
        if (!activeEl?.matches('[data-notes-cell="true"]')) {
          return;
        }
        event.preventDefault();
        navigator.clipboard.readText().then(text => {
          if (text) {
            pasteHandlerRef.current(text);
          }
        });
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // End drag on global mouseup
  useEffect(() => {
    const onMouseUp = () => {
      isDraggingRef.current = false;
    };
    document.addEventListener('mouseup', onMouseUp);
    return () => document.removeEventListener('mouseup', onMouseUp);
  }, []);

  // Clear selection when clicking outside any note cell
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-notes-cell="true"]')) {
        setSelectedNoteIds(new Set());
        setLastSelectedNoteId(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const {
    rows,
    isLoading,
    updateValuationStatus,
    updateValuationNotes,
    updateValuationEngagementManager,
    selectedUserEmail,
    setSelectedUserEmail,
    staffUsers,
    isLoadingStaffUsers,
  } = useValuationsWithBulkData({
    filters,
    orderBy,
    order,
    sortCriteria,
    pauseBatchLoading,
    searchParams: {
      searchParam: debouncedSearchParam,
      searchAll: debouncedSearchParam.trim() ? searchParam.searchAll : false,
    },
  });

  // Track color palette changes to force re-render when Done is clicked
  const [, setPaletteVersion] = useState(0);

  const handlePaletteClose = useCallback(() => {
    setPaletteVersion(prev => prev + 1);
  }, []);

  const {
    isNoteLoading,
    handleNoteSave: handleNoteSaveGeneric,
  } = useNoteHandler({
    onSuccess: (id, noteContent, createdNote) => {
      const noteId = createdNote?.id;
      if (createdNote?.attio_valuation_id) {
        updateValuationNotes(createdNote.attio_valuation_id, noteContent, noteId);
      } else {
        updateValuationNotes(id as string, noteContent, noteId);
      }
    },
  });

  const handleNoteSave = useCallback(
    async (valuationId: string, noteContent: string, currentNote?: string) => {
      const request: CreateNoteRequest = {
        content: noteContent,
        attio_valuation_id: valuationId,
        reference_type: 'at',
      };

      const currentValuation = rows.find(r => r.record_id === valuationId);
      const hasExistingNote = Boolean(
        (currentNote && currentNote.trim() !== '' && currentNote !== '-') ||
          (currentValuation?.note?.content && currentValuation.note.content.trim() !== '')
      );

      let existingNoteId: string | undefined;

      if (hasExistingNote) {
        const noteId = currentValuation?.note?.id;
        if (noteId && noteId !== 0) {
          existingNoteId = noteId.toString();
        } else {
          existingNoteId = undefined;
        }
      }

      await handleNoteSaveGeneric(valuationId, noteContent, currentNote, request, hasExistingNote, existingNoteId);
    },
    [rows, handleNoteSaveGeneric]
  );

  const getDefaultVisibility = useCallback((): Record<string, boolean> => {
    const initialVisibility: Record<string, boolean> = {};
    columns.forEach(col => {
      initialVisibility[col.id] = !COLUMNS_HIDDEN_BY_DEFAULT.includes(col.id);
    });
    return initialVisibility;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const storageKey = sectionId ? `valuations_column_visibility_${sectionId}` : '';
  const [, setStoredVisibility] = useLocalStorage<Record<string, boolean>>(
    storageKey || 'temp_key',
    getDefaultVisibility()
  );

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    if (sectionId) {
      return getInitialColumnVisibilityFromStorage(sectionId, columns as EnhancedTableHeadColumn[]);
    }
    return getDefaultVisibility();
  });

  // Sync columnVisibility with storedVisibility when sectionId changes
  useEffect(() => {
    if (sectionId) {
      if (sectionId !== previousSectionId.current) {
        isInitialMount.current = true;
        previousSectionId.current = sectionId;
      }

      const savedVisibility = getInitialColumnVisibilityFromStorage(sectionId, columns as EnhancedTableHeadColumn[]);
      setColumnVisibility(savedVisibility);
    } else {
      setColumnVisibility(getDefaultVisibility());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, getDefaultVisibility]);

  // Update localStorage when columnVisibility changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (sectionId) {
      saveColumnVisibilityToStorage(sectionId, columnVisibility, setStoredVisibility);
    }
  }, [columnVisibility, sectionId, setStoredVisibility]);

  const [columnWidths, setColumnWidths] = useLocalStorage<Record<string, number>>('columnWidths', {});

  const handleColumnWidthChange = useCallback(
    (columnId: string, newWidth: number) => {
      setColumnWidths(prev => ({
        ...prev,
        [columnId]: newWidth,
      }));
    },
    [setColumnWidths]
  );

  const conditionalColumns = useMemo(
    () => columns.filter(col => columnVisibility[col.id] !== false),
    [columnVisibility]
  );

  // Check if any financial column is visible (triggers horizontal scroll)
  const hasExtraColumns = useMemo(
    () => conditionalColumns.some(col => FINANCIAL_FIELDS_HIDDEN_BY_DEFAULT.includes(col.id)),
    [conditionalColumns]
  );

  const allRows = rows;

  const [filteredRows, setFilteredRows] = useState<SimplifiedAttioValuation[]>(allRows);
  const [tableKey, setTableKey] = useState(0);

  const { focusedRowIndex, setFocusedRowIndex, handleKeyDown } = useKeyboardNavigation(filteredRows.length);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const hasAutoFocused = useRef(false);

  useEffect(() => {
    setFilteredRows(allRows);
  }, [allRows]);

  // Auto-focus the first cell when rows first become available.
  // Reset when rows empty out so the next load re-triggers.
  useEffect(() => {
    if (filteredRows.length === 0) {
      hasAutoFocused.current = false;
      return;
    }
    if (hasAutoFocused.current) {
      return;
    }
    hasAutoFocused.current = true;
    const firstCell = tbodyRef.current?.querySelector<HTMLElement>('tr[tabindex] td');
    if (firstCell) {
      if (!firstCell.hasAttribute('tabindex')) {
        firstCell.setAttribute('tabindex', '-1');
      }
      firstCell.focus();
    }
  }, [filteredRows.length]);

  // ── Note selection handlers (placed after filteredRows is available) ───
  const handleNoteSelect = useCallback(
    (recordId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (e.shiftKey && lastSelectedNoteId) {
        const startIdx = filteredRows.findIndex(r => r.record_id === lastSelectedNoteId);
        const endIdx = filteredRows.findIndex(r => r.record_id === recordId);
        const [from, to] = startIdx <= endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
        setSelectedNoteIds(new Set(filteredRows.slice(from, to + 1).map(r => r.record_id)));
      } else {
        setSelectedNoteIds(new Set([recordId]));
        setLastSelectedNoteId(recordId);
      }
    },
    [filteredRows, lastSelectedNoteId]
  );

  const handleNoteDoubleClick = useCallback((recordId: string) => {
    setSelectedNoteIds(new Set([recordId]));
    setLastSelectedNoteId(recordId);
  }, []);

  const handleNoteMouseDown = useCallback((recordId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    isDraggingRef.current = true;
    dragAnchorIdRef.current = recordId;
    if (!e.shiftKey) {
      setSelectedNoteIds(new Set([recordId]));
      setLastSelectedNoteId(recordId);
    }
  }, []);

  const handleNoteMouseEnter = useCallback(
    (recordId: string) => {
      if (!isDraggingRef.current || !dragAnchorIdRef.current) {
        return;
      }
      const startIdx = filteredRows.findIndex(r => r.record_id === dragAnchorIdRef.current);
      const endIdx = filteredRows.findIndex(r => r.record_id === recordId);
      const [from, to] = startIdx <= endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
      setSelectedNoteIds(new Set(filteredRows.slice(from, to + 1).map(r => r.record_id)));
    },
    [filteredRows]
  );

  const handlePasteToSelected = useCallback(
    async (text: string) => {
      if (!text.trim()) {
        return;
      }
      const activeEl = document.activeElement as HTMLElement | null;
      const focusedId = activeEl?.dataset?.recordId;
      let targetIds: Set<string> | null;
      if (selectedNoteIds.size > 0) {
        targetIds = selectedNoteIds;
      } else if (focusedId) {
        targetIds = new Set([focusedId]);
      } else {
        targetIds = null;
      }
      if (!targetIds) {
        return;
      }
      await Promise.all(
        Array.from(targetIds).map(async recordId => {
          const row = filteredRows.find(r => r.record_id === recordId);
          if (row) {
            await handleNoteSave(recordId, text, row.note?.content || '');
          }
        })
      );
    },
    [selectedNoteIds, filteredRows, handleNoteSave]
  );

  useEffect(() => {
    pasteHandlerRef.current = handlePasteToSelected;
  }, [handlePasteToSelected]);

  const handleAutoFitColumns = useCallback(() => {
    // Calculate optimal width for Company Name based on longest company name in data
    const longestCompanyName = filteredRows.reduce((longest, row) => {
      const name = row.company_name || '';
      return name.length > longest.length ? name : longest;
    }, '');

    // Calculate width: ~8px per character + padding (cell padding + some buffer)
    const charWidth = 8;
    const padding = 32;
    const companyNameWidth = Math.max(150, Math.min(400, longestCompanyName.length * charWidth + padding));

    // Clear all custom column widths and set Company Name to calculated width
    setColumnWidths({
      [COMPANY_NAME_KEY]: companyNameWidth,
    });

    // Force table remount to recalculate column widths
    setTableKey(prev => prev + 1);
  }, [setColumnWidths, filteredRows]);

  const { applyFilters, isSearching } = useAttioFilters(allRows, setFilteredRows);

  const { data: attioStatusOptions = [], isLoading: isLoadingStatusOptions } = useQuery({
    queryKey: [AttioQueryKeys.VALUATION_STATUS_OPTIONS],
    queryFn: getValuationStatusOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  const { data: dealStageOptionsData = [] } = useQuery<Array<{ id?: { status_id?: string }; title?: string }>>({
    queryKey: [AttioQueryKeys.DEAL_STAGE_OPTIONS],
    queryFn: async () => {
      const response = await getDealStatusOptions();
      return (response.data || []) as Array<{ id?: { status_id?: string }; title?: string }>;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  const statusOptions = attioStatusOptions.map(option => ({
    id: {
      workspace_id: option.id.workspace_id,
      object_id: option.id.object_id,
      attribute_id: option.id.attribute_id,
      option_id: option.id.option_id,
    },
    title: option.title,
    is_archived: option.is_archived,
  }));
  const filterStatusOptions = statusOptions.filter(option => rows.some(row => row.status === option.title));
  const statusOptionsLoading = isLoadingStatusOptions;

  const user = useSelector((state: RootStateType) => state.user);
  const { zoomLevel } = useZoom();
  const tableHeight = useMemo(() => calculateTableHeight(zoomLevel, 200), [zoomLevel]);

  // Subscribe to valuation updates from OperationsModal
  useEventSubscription<ValuationsTableValuationUpdatedPayload>(
    EventType.WORKBOARD_VALUATION_UPDATED,
    useCallback(
      (payload: ValuationsTableValuationUpdatedPayload) => {
        const { valuationId, updates } = payload;

        const applyUpdatesToRow = (row: SimplifiedAttioValuation): SimplifiedAttioValuation => {
          if (row.record_id !== valuationId) {
            return row;
          }
          const updatedRow = { ...row };
          if (updates.status) {
            updatedRow.status = updates.status;
          }
          if (updates.dealStage) {
            updatedRow.deal_stage = updates.dealStage;
          }
          if (updates.deal) {
            if (updates.deal.value) {
              updatedRow.deal = updates.deal.value;
            }
            if (updates.deal.due) {
              updatedRow.deal_due = updates.deal.due;
            }
            if (updates.deal.stage) {
              updatedRow.deal_stage = updates.deal.stage;
            }
          }
          return updatedRow;
        };

        setFilteredRows(prevRows => prevRows.map(applyUpdatesToRow));
        updateValuationStatus(valuationId, updates.status || '');
      },
      [updateValuationStatus]
    )
  );

  const handleDealStageChange = useCallback(
    async (row: SimplifiedAttioValuation, dealStageId: string) => {
      if (!row.deal_id) {
        enqueueSnackbar('Cannot update deal stage: deal_id is missing', { variant: 'error' });
        return;
      }

      const dealId = row.deal_id;
      try {
        await updateValuationDealStage(dealId, dealStageId);

        // Find the deal stage label from the options (same structure as ValuationDealInfo expects)
        const selectedOption = dealStageOptionsData.find(option => option.id?.status_id === dealStageId);
        const newDealStage = selectedOption?.title || row.deal_stage;

        // Update local state
        setFilteredRows(prevRows =>
          prevRows.map(r => (r.record_id === row.record_id ? { ...r, deal_stage: newDealStage } : r))
        );
      } catch (error) {
        Logger.error('Error updating deal stage', error);
      }
    },
    [dealStageOptionsData]
  );

  const handleRowClick = (row: SimplifiedAttioValuation, event: React.MouseEvent<HTMLTableRowElement>) => {
    onRowClick(row, event);
  };

  const handleColumnsToggle = (columnId: string) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <TableRow>
          <StyledTableCellEmptyMessage colSpan={conditionalColumns.length + 3}>
            <CircularProgress size={24} />
          </StyledTableCellEmptyMessage>
        </TableRow>
      );
    }

    if (filteredRows.length === 0) {
      return (
        <TableRow>
          <StyledTableCellEmptyMessage colSpan={conditionalColumns.length + 3}>
            {EMPTY_VALUATIONS_TABLE}
          </StyledTableCellEmptyMessage>
        </TableRow>
      );
    }

    return filteredRows.map((row, i) => (
      <ValuationsTableRow
        key={row.record_id}
        valuationRow={row}
        onRowClick={handleRowClick}
        onStatusUpdate={updatedRow => updateValuationStatus(updatedRow.record_id, updatedRow.status || '')}
        onNoteUpdate={updatedRow => updateValuationNotes(updatedRow.record_id, updatedRow.note?.content || '')}
        onEngagementManagerUpdate={updateValuationEngagementManager}
        onNoteSave={noteContent => {
          const currentNoteContent = row.note?.content || '';
          return handleNoteSave(row.record_id, noteContent, currentNoteContent);
        }}
        statusOptions={statusOptions}
        statusOptionsLoading={statusOptionsLoading}
        isNoteLoading={isNoteLoading}
        columns={conditionalColumns as EnhancedTableHeadColumn[]}
        columnWidths={columnWidths}
        onDealStageChange={async (dealStageId: string) => {
          await handleDealStageChange(row, dealStageId);
        }}
        dealStageOptions={dealStageOptionsData as DealStageOption[]}
        tabIndex={focusedRowIndex === i ? 0 : -1}
        rowIndex={i}
        onFocus={setFocusedRowIndex}
        isNoteSelected={selectedNoteIds.has(row.record_id)}
        onNoteSelect={handleNoteSelect}
        onNoteDoubleClick={handleNoteDoubleClick}
        onNoteMouseDown={handleNoteMouseDown}
        onNoteMouseEnter={handleNoteMouseEnter}
      />
    ));
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={1} sx={{ mt: 2, mx: 2, justifyContent: 'space-between' }}>
        <Box display="flex" alignItems="center" gap={1}>
          {toolbarPrefix}
          <ConfigToggler
            columns={columns as EnhancedTableHeadColumn[]}
            columnVisibility={columnVisibility}
            onColumnsToggle={handleColumnsToggle}
            onPaletteClose={handlePaletteClose}
          />
          {!hideTable && (
            <Tooltip title="Auto-fit Columns">
              <IconButton size="small" onClick={handleAutoFitColumns}>
                <FitScreenOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <ExpandableSearch
            isValuationsPage
            ref={searchRef}
            value={searchParam.searchParam}
            onChange={e => setSearchParam(prev => ({ ...prev, searchParam: e.target.value }))}
            placeholder="Search by company..."
            searchAll={searchParam.searchAll}
            onSearchAllChange={v => setSearchParam(prev => ({ ...prev, searchAll: v }))}
          />
          <JobFilters
            filters={filters}
            statusOptions={filterStatusOptions}
            statusOptionsLoading={statusOptionsLoading}
            onFiltersChange={applyFilters}
            searchParams={{ searchParam: debouncedSearchParam, searchThroughEveryDealAndValuation: searchParam.searchAll }}
            onClearSearch={() => setSearchParam(prev => ({ ...prev, searchParam: '' }))}
          />
          {user?.is_staff && (
            <StyledUserAutocomplete
              size="small"
              options={staffUsers}
              getOptionLabel={(option: SlimUser) => option.email || ''}
              isOptionEqualToValue={(option: SlimUser, value: SlimUser) => option.email === value.email}
              value={
                selectedUserEmail
                  ? staffUsers.find((staffUser: SlimUser) => staffUser.email === selectedUserEmail) || null
                  : null
              }
              onChange={(_event, newValue: SlimUser | null) => {
                setSelectedUserEmail(newValue?.email || '');
              }}
              disabled={isLoadingStaffUsers}
              slotProps={userAutocompleteSlotProps}
              renderInput={params => <TextField {...params} placeholder="Select User" />}
            />
          )}
        </Box>
        <Box marginRight={2}>
          {isLoading ? (
            <Skeleton variant="text" width={150} height={24} />
          ) : (
            <Typography variant="body1">Total valuations: {filteredRows.length.toLocaleString()}</Typography>
          )}
        </Box>
      </Box>
      {!hideTable && <TableContainer
        sx={{
          overflowX: hasExtraColumns ? 'auto' : 'hidden',
          overflowY: 'auto',
          maxWidth: '100%',
          height: tableHeight,
          position: 'relative',
        }}>
        {isSearching && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
            }}>
            <CircularProgress size={32} />
          </Box>
        )}
        <Table
          key={tableKey}
          sx={{
            minWidth: 650,
            tableLayout: 'auto',
            width: hasExtraColumns ? 'max-content' : '100%',
          }}>
          <ValuationsTableHead
            orderBy={orderBy}
            order={order}
            sortCriteria={sortCriteria}
            onRequestSort={onRequestSort}
            columns={conditionalColumns as EnhancedTableHeadColumn[]}
            columnWidths={columnWidths}
            onColumnWidthChange={handleColumnWidthChange}
          />
          <TableBody ref={tbodyRef} onKeyDown={handleKeyDown}>{renderTableBody()}</TableBody>
        </Table>
      </TableContainer>}
    </>
  );
};
